/**
 * FloatingContactHub — Bouton flottant unique qui déplie 2 options :
 *   1. WhatsApp direct
 *   2. Chat "Laurent IA" (limité 3 questions / 24h par IP, endpoint /api/ai-coach/chat-guest)
 *
 * Affiché globalement (App.js) sur toutes les pages SAUF /calculateur-calories
 * (qui a son propre AICoachGuestChat contextuel avec question proactive).
 */
import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, Send, Loader2, MessageCircle } from 'lucide-react';
import { WHATSAPP_URL } from '../data/constants';
import { trackWhatsApp, trackEvent } from '../hooks/useAnalytics';

const API = process.env.REACT_APP_BACKEND_URL;

const WhatsAppIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const FloatingContactHub = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  // Auto-scroll dans le chat
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  // Ne pas afficher sur /calculateur-calories (AICoachGuestChat déjà présent) ni sur /admin
  const hidden = location.pathname.startsWith('/calculateur-calories') || location.pathname.startsWith('/admin');
  if (hidden) return null;

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const newHistory = [...messages, { role: 'user', content: text }];
    setMessages(newHistory);
    setInput('');
    setLoading(true);
    trackEvent('ai_chat_message_sent', { source: 'floating_hub' });
    try {
      // Le backend attend `messages: [{role, content}]` (voir /api/ai-coach/chat-guest)
      const payload = {
        messages: newHistory
          .filter((m) => m.role === 'user' || m.role === 'assistant')
          .map((m) => ({ role: m.role, content: m.content })),
        calc_context: {},
      };
      const res = await fetch(`${API}/api/ai-coach/chat-guest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.status === 429) {
        setMessages((m) => [...m, { role: 'assistant', content: "Merci d'avoir échangé avec moi ! Tu as atteint la limite de questions gratuites pour aujourd'hui. Contacte Laurent directement via WhatsApp ou postule pour un appel gratuit 🎯" }]);
      } else if (!res.ok) {
        throw new Error('backend');
      } else {
        const data = await res.json();
        if (data.limit_reached) {
          setMessages((m) => [...m, { role: 'assistant', content: data.message || "Limite atteinte pour aujourd'hui. Reviens demain ou contacte Laurent sur WhatsApp 🎯" }]);
        } else {
          setMessages((m) => [...m, { role: 'assistant', content: data.message }]);
        }
      }
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', content: "Désolé, un problème technique — réessaie dans un instant ou contacte-moi sur WhatsApp !" }]);
    } finally {
      setLoading(false);
    }
  };

  const openChat = () => {
    setChatOpen(true);
    setOpen(false);
    trackEvent('ai_chat_opened', { source: 'floating_hub' });
    if (messages.length === 0) {
      setMessages([{ role: 'assistant', content: "Salut ! Je suis Laurent IA, l'assistant du site Body-Up. Je peux répondre à tes questions sur le coaching, la nutrition, les tarifs, la prise de RDV ou tout ce qui concerne Body-Up. Pose-moi ta question ! 💪" }]);
    }
  };

  return (
    <>
      {/* Chat window */}
      <AnimatePresence>
        {chatOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-6 right-6 z-[60] w-[380px] max-w-[calc(100vw-24px)] h-[560px] max-h-[calc(100vh-80px)] bg-gradient-to-br from-zinc-900 to-black border border-primary/30 rounded-3xl shadow-2xl shadow-primary/30 overflow-hidden flex flex-col"
            data-testid="floating-chat-window"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-primary/20 to-pink-500/10 border-b border-white/10">
              <div className="flex items-center gap-2">
                <div className="relative w-9 h-9 rounded-full bg-gradient-to-br from-primary to-pink-500 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-zinc-900" />
                </div>
                <div>
                  <div className="text-sm font-bold text-white">Laurent IA</div>
                  <div className="text-[10px] text-zinc-400">Assistant Body-Up · En ligne</div>
                </div>
              </div>
              <button type="button" onClick={() => setChatOpen(false)} className="p-1.5 rounded-lg hover:bg-white/10 text-zinc-400 hover:text-white transition-colors" data-testid="floating-chat-close" aria-label="Fermer">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${m.role === 'user' ? 'bg-gradient-to-br from-primary to-pink-500 text-white rounded-br-md' : 'bg-white/5 border border-white/10 text-zinc-100 rounded-bl-md'}`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white/5 border border-white/10 px-3.5 py-2.5 rounded-2xl rounded-bl-md text-zinc-400 text-sm inline-flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Laurent IA réfléchit…
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-white/10 p-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder="Pose ta question…"
                  disabled={loading}
                  data-testid="floating-chat-input"
                  className="flex-1 px-3 py-2 rounded-xl bg-white/5 border border-white/10 focus:border-primary focus:outline-none text-white text-sm placeholder-zinc-500"
                />
                <button type="button" onClick={sendMessage} disabled={loading || !input.trim()} data-testid="floating-chat-send" className="p-2 rounded-xl bg-gradient-to-br from-primary to-pink-500 hover:from-pink-500 hover:to-primary text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <p className="text-[10px] text-zinc-500 mt-1.5 px-1">Chat gratuit — 3 questions par jour. Pour plus, prends un vrai RDV avec Laurent 🎯</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fan menu (sub-actions révélées au clic sur le bouton principal) */}
      <AnimatePresence>
        {open && !chatOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed bottom-24 right-6 z-[55] flex flex-col items-end gap-2.5"
            data-testid="floating-hub-menu"
          >
            {/* Chat IA */}
            <motion.button
              type="button"
              onClick={openChat}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              transition={{ duration: 0.18, delay: 0.05 }}
              className="group inline-flex items-center gap-3"
              data-testid="floating-hub-openchat"
              aria-label="Chatter avec Laurent IA"
            >
              <span className="px-3 py-1.5 rounded-xl bg-black/80 backdrop-blur border border-white/10 text-white text-xs font-semibold whitespace-nowrap shadow-lg">
                Chatter avec Laurent IA
              </span>
              <span className="relative w-12 h-12 rounded-full bg-gradient-to-br from-primary to-pink-500 flex items-center justify-center shadow-lg shadow-primary/40 group-hover:scale-110 transition-transform">
                <Sparkles className="w-5 h-5 text-white" />
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-black" />
              </span>
            </motion.button>

            {/* WhatsApp */}
            <motion.a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => { setOpen(false); trackWhatsApp && trackWhatsApp(); }}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              transition={{ duration: 0.18 }}
              className="group inline-flex items-center gap-3"
              data-testid="floating-hub-whatsapp"
              aria-label="Contacter sur WhatsApp"
            >
              <span className="px-3 py-1.5 rounded-xl bg-black/80 backdrop-blur border border-white/10 text-white text-xs font-semibold whitespace-nowrap shadow-lg">
                WhatsApp direct
              </span>
              <span className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg shadow-green-500/40 group-hover:scale-110 transition-transform">
                <WhatsAppIcon className="w-6 h-6 text-white" />
              </span>
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main hub button */}
      {!chatOpen && (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="fixed bottom-6 right-6 z-[55] group"
          data-testid="floating-hub-btn"
          aria-label={open ? "Fermer le menu de contact" : "Ouvrir le menu de contact"}
          aria-expanded={open}
        >
          <span className="absolute inset-0 bg-gradient-to-br from-primary to-pink-500 rounded-full blur-xl opacity-50 group-hover:opacity-80 animate-pulse transition-opacity" />
          <span className="relative flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-primary to-pink-500 text-white shadow-2xl shadow-primary/40 hover:scale-110 transition-transform">
            <AnimatePresence mode="wait" initial={false}>
              {open ? (
                <motion.span key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}>
                  <X className="w-6 h-6" />
                </motion.span>
              ) : (
                <motion.span key="chat" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}>
                  <MessageCircle className="w-6 h-6" />
                </motion.span>
              )}
            </AnimatePresence>
          </span>
          {!open && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-ping" aria-hidden="true" />
          )}
        </button>
      )}
    </>
  );
};

export default FloatingContactHub;
